import {
    ControlsItem,
    Input,
    PageContent,
    Preloader,
    SearchIcon,
    Tab,
    Tabs,
    Typography
} from '@genestack/ui';
import React from 'react';
import {GraphViewKind} from '../../../../../../components/graph/interface';
import {ContinuantsStatistics} from './continuants-statistics';
import {ProcessesStatistics} from './processes-statistics';
import {useFoldingItemsState} from '../../../../../../hooks/use-folding-items-state';
import {useGetQueryStats} from './use-get-query-stats';
import {SidebarHeader} from '../../../../../../components/layout/sidebar/sidebar-header/sidebar-header';
import styles from '../right-panel.module.css';
import {useFilterStats} from './use-filter-stats';

interface Props {
    queryId: number;
    viewKind: GraphViewKind;
    maxPathLength: number;
    selectedGraphNodes: number[];
    selectGraphNodes: (nodes: number[], isSelectMultiple: boolean) => void;
    setHoveredNodes: (nodeIds: number[]) => void;
    closePanel: () => void;
    invisibleNodes: number[];
}

enum StatisticsTabs {
    CONTINUANTS = 'CONTINUANTS',
    PROCESSES = 'PROCESSES'
}

const StatisticsComponent = (props: Props) => {
    const [selectedTab, setSelectedTab] = React.useState(StatisticsTabs.CONTINUANTS);
    const [searchValue, setSearchValue] = React.useState('');
    const queryStats = useGetQueryStats(
        props.queryId,
        props.viewKind,
        props.maxPathLength,
        props.invisibleNodes
    );
    const continuantsFoldingStateProps = useFoldingItemsState();
    const processesFoldingStateProps = useFoldingItemsState();
    const searchContinuantsFoldingStateProps = useFoldingItemsState();
    const searchProcessesFoldingStateProps = useFoldingItemsState();
    const continuantsIrrelevantShownProps = useFoldingItemsState();
    const processesIrrelevantShownProps = useFoldingItemsState();
    const filteredStats = useFilterStats({stats: queryStats.data, searchString: searchValue});

    React.useEffect(() => {
        searchContinuantsFoldingStateProps.clear();
        searchProcessesFoldingStateProps.clear();
        continuantsIrrelevantShownProps.clear();
        processesIrrelevantShownProps.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    const handleInputClearButtonClick = () => {
        setSearchValue('');
    };

    return (
        <React.Fragment>
            <SidebarHeader
                title={
                    <ControlsItem className={styles.searchBar}>
                        <Input
                            clearable={!!searchValue}
                            autoFocus
                            fullWidth
                            value={searchValue}
                            onValueChange={setSearchValue}
                            placeholder="Search"
                            prepend={<SearchIcon />}
                            onClearButtonClick={handleInputClearButtonClick}
                        />
                    </ControlsItem>
                }
                onClose={props.closePanel}
            >
                <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                    <Tab value={StatisticsTabs.CONTINUANTS}>
                        Continuants{' '}
                        {filteredStats.hasSearchResults && (
                            <Typography intent="quiet" box="inline">
                                {filteredStats.filteredContinuantsNumber}
                            </Typography>
                        )}
                    </Tab>
                    <Tab value={StatisticsTabs.PROCESSES}>
                        Processes{' '}
                        {filteredStats.hasSearchResults && (
                            <Typography intent="quiet" box="inline">
                                {filteredStats.filteredProcessesNumber}
                            </Typography>
                        )}
                    </Tab>
                </Tabs>
            </SidebarHeader>

            {(function () {
                if (filteredStats.isNothingFound) {
                    return (
                        <PageContent>
                            <div className={styles.nothingFoundView}>
                                No results for "{searchValue}"
                            </div>
                        </PageContent>
                    );
                }

                if (!filteredStats.filteredContinuantsGroups || !filteredStats.filteredProcesses) {
                    return (
                        <PageContent>
                            <Preloader show count={5} box="paragraph" />
                        </PageContent>
                    );
                }

                if (selectedTab === StatisticsTabs.CONTINUANTS) {
                    return (
                        <ContinuantsStatistics
                            selectedGraphNodes={props.selectedGraphNodes}
                            selectGraphNodes={props.selectGraphNodes}
                            setHoveredNodes={props.setHoveredNodes}
                            foldingStateProps={
                                searchValue
                                    ? searchContinuantsFoldingStateProps
                                    : continuantsFoldingStateProps
                            }
                            groupedContinuantsMap={filteredStats.filteredContinuantsGroups}
                            searchString={searchValue}
                            searchResultChildrenMaps={filteredStats.searchResultChildrenMaps}
                            irrelevantShownProps={continuantsIrrelevantShownProps}
                        />
                    );
                }

                return (
                    <ProcessesStatistics
                        processes={filteredStats.filteredProcesses}
                        foldingStateProps={
                            searchValue
                                ? searchProcessesFoldingStateProps
                                : processesFoldingStateProps
                        }
                        selectedGraphNodes={props.selectedGraphNodes}
                        selectGraphNodes={props.selectGraphNodes}
                        setHoveredNodes={props.setHoveredNodes}
                        searchString={searchValue}
                        searchResultChildrenMaps={filteredStats.searchResultChildrenMaps}
                        irrelevantShownProps={processesIrrelevantShownProps}
                    />
                );
            })()}
        </React.Fragment>
    );
};

export const Statistics = React.memo(StatisticsComponent);
